import { z } from "zod";

import { calculationRunSummarySchema } from "./calculationRun";
import { jurisdictionProfileSchema } from "./cableContract";
import { exactDecimalSchema } from "./faultContract";
import { CHOOSE_A_FAULT_RUN, formatApiError, requestSignal } from "./faultLink";
import { ApiError } from "./http";
import { CHOOSE_A_LOAD_RUN } from "./loadLink";
import {
  harmonicOrderSchema,
  linkedLoadRunResponseSchema,
  powerQualityWarningSchema,
  shortCircuitPowerResponseSchema,
  typedShortCircuitPowerSchema,
} from "./pfCorrection";
import {
  demandCurrentSourceSchema,
  limitBasisSchema,
  limitComparisonSchema,
  powerQualityStatusSchema,
} from "./powerQualityContract";

// The harmonic assessment at one point of common coupling (EOS-11b, Master Prompt A32):
// the request as the form sends it, the response as the engine answers it, and the
// persisted run. The backend mirror is app/schemas/power_quality.py
// (HarmonicAssessmentRequest, HarmonicAssessmentResponse).

const optionalRequestText = (maxLength: number) =>
  z.string().trim().max(maxLength).nullable().optional();

const RUNS_URL = "/api/v1/electrical/harmonics/runs";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// One harmonic current of a load: its order and the amperes, or a percent of I1 - one of
// the two, never both (the backend converts a percent exactly, I_h = I1 x percent / 100).
export const harmonicComponentRequestSchema = z
  .object({
    order: harmonicOrderSchema,
    current_a: exactDecimalSchema.optional(),
    percent_of_fundamental: exactDecimalSchema.optional(),
  })
  .strict()
  .superRefine((values, ctx) => {
    if ((values.current_a === undefined) === (values.percent_of_fundamental === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "give each harmonic component as amperes or as a percent, not both and not neither",
        path: ["current_a"],
      });
    }
  });

// One distorting load with the spectrum the engineer declares and its source; the product
// ships no spectrum of its own, a blank source is a review.
export const distortingLoadRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    fundamental_current_a: exactDecimalSchema,
    spectrum: z.array(harmonicComponentRequestSchema).max(50).optional(),
    spectrum_source: optionalRequestText(200),
    spectrum_source_date: optionalRequestText(50),
  })
  .strict();

// The maximum demand current IL typed by the engineer: allowed, not linked evidence.
export const typedDemandCurrentSchema = z
  .object({
    maximum_demand_current_a: exactDecimalSchema,
    source: optionalRequestText(200),
  })
  .strict();

// The project's harmonic limit as the engineer declares it with its source (D2).
export const declaredHarmonicLimitRequestSchema = z
  .object({
    thdv_percent: exactDecimalSchema.optional(),
    tdd_percent: exactDecimalSchema.optional(),
    source: optionalRequestText(200),
  })
  .strict();

export const harmonicAssessmentRequestSchema = z
  .object({
    code: z.string().trim().min(1).max(50),
    name: z.string().trim().min(1).max(200),
    bus_code: z.string().trim().min(1).max(50),
    system_nominal_voltage_v: exactDecimalSchema,
    frequency_hz: exactDecimalSchema,
    loads: z.array(distortingLoadRequestSchema).min(1).max(50),
    // At most one of the two; neither means TDD and Isc / IL are not made (a review).
    load_run_id: z.string().optional(),
    typed_demand: typedDemandCurrentSchema.optional(),
    // At most one of the two; neither means the voltages and THDv are not made.
    fault_run_id: z.string().optional(),
    typed_short_circuit_power: typedShortCircuitPowerSchema.optional(),
    declared_limit: declaredHarmonicLimitRequestSchema.optional(),
    jurisdiction_profile: jurisdictionProfileSchema.optional(),
    notes: optionalRequestText(1000),
  })
  .strict()
  .superRefine((values, ctx) => {
    if (values.load_run_id !== undefined && values.typed_demand !== undefined) {
      ctx.addIssue({
        code: "custom",
        message: "give at most one demand current source: load_run_id or typed_demand",
        path: ["load_run_id"],
      });
    } else if (values.load_run_id !== undefined && !UUID.test(values.load_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_LOAD_RUN, path: ["load_run_id"] });
    }

    if (values.fault_run_id !== undefined && values.typed_short_circuit_power !== undefined) {
      ctx.addIssue({
        code: "custom",
        message:
          "give at most one short-circuit power source: fault_run_id or typed_short_circuit_power",
        path: ["fault_run_id"],
      });
    } else if (values.fault_run_id !== undefined && !UUID.test(values.fault_run_id)) {
      ctx.addIssue({ code: "custom", message: CHOOSE_A_FAULT_RUN, path: ["fault_run_id"] });
    }

    const codes = values.loads.map((load) => load.code);
    if (new Set(codes).size !== codes.length) {
      ctx.addIssue({
        code: "custom",
        message: "loads must not repeat a code",
        path: ["loads"],
      });
    }
  });

export const harmonicComponentResponseSchema = z
  .object({ order: z.number().int(), current_a: z.string() })
  .strict();

// A load as the engine saw it: the spectrum in amperes, a percent declaration resolved.
export const distortingLoadResponseSchema = z
  .object({
    code: z.string(),
    fundamental_current_a: z.string(),
    spectrum: z.array(harmonicComponentResponseSchema),
    spectrum_source: z.string().nullable(),
    spectrum_source_date: z.string().nullable(),
  })
  .strict();

export const harmonicOrderResponseSchema = z
  .object({
    order: z.number().int(),
    current_a: z.string(),
    voltage_v: z.string().nullable(),
    voltage_percent: z.string().nullable(),
  })
  .strict();

// The demand current and the short-circuit power echoed with where they came from, the
// loads in amperes, each figure the engine's exact string, the Isc basis and the method
// note with the result; null where a figure was not made.
export const harmonicAssessmentResponseSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    bus_code: z.string(),
    system_nominal_voltage_v: z.string(),
    frequency_hz: z.string(),
    loads: z.array(distortingLoadResponseSchema),
    load_codes: z.array(z.string()),
    maximum_demand_current_a: z.string().nullable(),
    demand_current_source: demandCurrentSourceSchema.nullable(),
    demand_current_source_text: z.string().nullable(),
    linked_load_run: linkedLoadRunResponseSchema.nullable(),
    short_circuit_power: shortCircuitPowerResponseSchema.nullable(),
    fundamental_current_a: z.string(),
    harmonic_current_rms_a: z.string(),
    orders: z.array(harmonicOrderResponseSchema),
    thdi_percent: z.string(),
    tdd_percent: z.string().nullable(),
    isc_il_ratio: z.string().nullable(),
    thdv_percent: z.string().nullable(),
    isc_basis: z.string().nullable(),
    limit_basis: limitBasisSchema,
    limit_source: z.string().nullable(),
    thdv_limit_percent: z.string().nullable(),
    tdd_limit_percent: z.string().nullable(),
    thdv_comparison: limitComparisonSchema,
    tdd_comparison: limitComparisonSchema,
    status: powerQualityStatusSchema,
    warnings: z.array(powerQualityWarningSchema),
    method_note: z.string(),
    jurisdiction_profile: jurisdictionProfileSchema,
    notes: z.string().nullable(),
  })
  .strict();

export const harmonicAssessmentRunCreateRequestSchema = z
  .object({
    study: harmonicAssessmentRequestSchema,
    project_revision_id: z.string().uuid().optional(),
    notes: optionalRequestText(2000),
  })
  .strict();

export const harmonicAssessmentRunResponseSchema = z
  .object({
    run: calculationRunSummarySchema,
    result: harmonicAssessmentResponseSchema,
  })
  .strict();

export type HarmonicComponentRequest = z.infer<typeof harmonicComponentRequestSchema>;
export type DistortingLoadRequest = z.infer<typeof distortingLoadRequestSchema>;
export type TypedDemandCurrent = z.infer<typeof typedDemandCurrentSchema>;
export type DeclaredHarmonicLimitRequest = z.infer<typeof declaredHarmonicLimitRequestSchema>;
export type HarmonicAssessmentRequest = z.infer<typeof harmonicAssessmentRequestSchema>;
export type HarmonicOrderResponse = z.infer<typeof harmonicOrderResponseSchema>;
export type HarmonicAssessmentResponse = z.infer<typeof harmonicAssessmentResponseSchema>;
export type HarmonicAssessmentRunCreateRequest = z.infer<
  typeof harmonicAssessmentRunCreateRequestSchema
>;
export type HarmonicAssessmentRunResponse = z.infer<typeof harmonicAssessmentRunResponseSchema>;

/**
 * Calculate a harmonic assessment and persist it as a run revision. The rules are the
 * power factor correction client's: the whole run body, the chosen revision beside it, an
 * unchanged study answered with HTTP 200 (A11).
 */
export async function createHarmonicAssessmentRun(
  payload: HarmonicAssessmentRunCreateRequest,
  signal?: AbortSignal,
  projectRevisionId?: string,
): Promise<HarmonicAssessmentRunResponse> {
  const validatedPayload = harmonicAssessmentRunCreateRequestSchema.parse(payload);

  const response = await fetch(RUNS_URL, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(
      projectRevisionId === undefined
        ? validatedPayload
        : { ...validatedPayload, project_revision_id: projectRevisionId },
    ),
    cache: "no-store",
    signal: requestSignal(signal),
  });

  const data: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(
      formatApiError(data, response.status, "Harmonic assessment"),
      response.status,
    );
  }

  const parsed = harmonicAssessmentRunResponseSchema.safeParse(data);

  if (!parsed.success) {
    throw new Error(
      "Unexpected response from the KES Electrical OS harmonic assessment runs API.",
    );
  }

  return parsed.data;
}
